// This file should never have any imports

import type { OptionSelectorButton } from '../models/types'

export default function getPresetButtons(preset: string): OptionSelectorButton[] {
  switch (preset) {
    case 'countries':
      return getCountries()
    case 'countries_with_codes':
      return getCountriesWithCodes()
    case 'states':
      return getStates()
    case 'states_with_dc':
      return getStates({ dc: true })
    case 'states_with_dc_and_territories':
      return getStates({ dc: true, territories: true })
    case 'states_with_outside_us':
      return getStates({ outside_us: true })
    case 'canadian_provinces':
      return getProvince({})
    case 'canadian_provinces_and_territories':
      return getProvince({ territories: true })
    case 'day_nums':
      return getDayNums()
    case 'months':
      return getMonths()
    case 'months_full_name':
      return getMonthsFull()
    case 'years':
      return getYears()
    default:
      return []
  }
}

function getStates({
  dc,
  territories,
  outside_us,
}: { dc?: boolean; territories?: boolean; outside_us?: boolean } = {}): OptionSelectorButton[] {
  const territoriesList = ['as', 'fm', 'gu', 'mh', 'mp', 'pr', 'pw', 'vi']

  return Object.entries({
    al: 'Alabama',
    ak: 'Alaska',
    as: 'American Samoa',
    az: 'Arizona',
    ar: 'Arkansas',
    ca: 'California',
    co: 'Colorado',
    ct: 'Connecticut',
    de: 'Delaware',
    dc: 'District Of Columbia',
    fm: 'Federated States Of Micronesia',
    fl: 'Florida',
    ga: 'Georgia',
    gu: 'Guam',
    hi: 'Hawaii',
    id: 'Idaho',
    il: 'Illinois',
    in: 'Indiana',
    ia: 'Iowa',
    ks: 'Kansas',
    ky: 'Kentucky',
    la: 'Louisiana',
    me: 'Maine',
    mh: 'Marshall Islands',
    md: 'Maryland',
    ma: 'Massachusetts',
    mi: 'Michigan',
    mn: 'Minnesota',
    ms: 'Mississippi',
    mo: 'Missouri',
    mt: 'Montana',
    ne: 'Nebraska',
    nv: 'Nevada',
    nh: 'New Hampshire',
    nj: 'New Jersey',
    nm: 'New Mexico',
    ny: 'New York',
    nc: 'North Carolina',
    nd: 'North Dakota',
    mp: 'Northern Mariana Islands',
    oh: 'Ohio',
    ok: 'Oklahoma',
    or: 'Oregon',
    pw: 'Palau',
    pa: 'Pennsylvania',
    pr: 'Puerto Rico',
    ri: 'Rhode Island',
    sc: 'South Carolina',
    sd: 'South Dakota',
    tn: 'Tennessee',
    tx: 'Texas',
    ut: 'Utah',
    vt: 'Vermont',
    vi: 'Virgin Islands',
    va: 'Virginia',
    wa: 'Washington',
    wv: 'West Virginia',
    wi: 'Wisconsin',
    wy: 'Wyoming',
    outside_us: 'Outside the US',
  })
    .map(([key, text]) => ({ id: key, key, text }))
    .filter(
      (b) =>
        (dc || b.key !== 'dc') &&
        (territories || !territoriesList.includes(b.key)) &&
        (outside_us || b.key !== 'outside_us')
    )
}

function getProvince({ territories }: { territories?: boolean } = {}): OptionSelectorButton[] {
  const territoriesList = ['nt', 'nu', 'yt']

  return Object.entries({
    ab: `Alberta`,
    bc: `British Columbia`,
    mb: `Manitoba`,
    nb: `New Brunswick`,
    nl: `Newfoundland and Labrador`,
    nt: `Northwest Territories`,
    ns: `Nova Scotia`,
    nu: `Nunavut`,
    on: `Ontario`,
    pe: `Prince Edward Island`,
    qc: `Quebec`,
    sk: `Saskatchewan`,
    yt: `Yukon`,
  })
    .map(([key, text]) => ({ id: key, key, text }))
    .filter((b) => territories || !territoriesList.includes(b.key))
}

function getCountries(): OptionSelectorButton[] {
  return [
    `Afghanistan`,
    `Albania`,
    `Algeria`,
    `American Samoa`,
    `Andorra`,
    `Angola`,
    `Anguilla`,
    `Antarctica`,
    `Antigua and Barbuda`,
    `Argentina`,
    `Armenia`,
    `Aruba`,
    `Australia`,
    `Austria`,
    `Azerbaijan`,
    `Bahamas`,
    `Bahrain`,
    `Bangladesh`,
    `Barbados`,
    `Belarus`,
    `Belgium`,
    `Belize`,
    `Benin`,
    `Bermuda`,
    `Bhutan`,
    `Bolivia, Plurinational State of`,
    `Bonaire, Sint Eustatius and Saba`,
    `Bosnia and Herzegovina`,
    `Botswana`,
    `Bouvet Island`,
    `Brazil`,
    `British Indian Ocean Territory`,
    `Brunei Darussalam`,
    `Bulgaria`,
    `Burkina Faso`,
    `Burundi`,
    `Cambodia`,
    `Cameroon`,
    `Canada`,
    `Cape Verde`,
    `Cayman Islands`,
    `Central African Republic`,
    `Chad`,
    `Chile`,
    `China`,
    `Christmas Island`,
    `Cocos (Keeling) Islands`,
    `Colombia`,
    `Comoros`,
    `Congo`,
    `Congo, the Democratic Republic of the`,
    `Cook Islands`,
    `Costa Rica`,
    `C√¥te d'Ivoire`,
    `Croatia`,
    `Cuba`,
    `Cura√ßao`,
    `Cyprus`,
    `Czech Republic`,
    `Denmark`,
    `Djibouti`,
    `Dominica`,
    `Dominican Republic`,
    `Ecuador`,
    `Egypt`,
    `El Salvador`,
    `Equatorial Guinea`,
    `Eritrea`,
    `Estonia`,
    `Ethiopia`,
    `Falkland Islands (Malvinas)Faroe Islands`,
    `Fiji`,
    `Finland`,
    `France`,
    `French Guiana`,
    `French Polynesia`,
    `French Southern Territories`,
    `Gabon`,
    `Gambia`,
    `Georgia`,
    `Germany`,
    `Ghana`,
    `Gibraltar`,
    `Greece`,
    `Greenland`,
    `Grenada`,
    `Guadeloupe`,
    `Guam`,
    `Guatemala`,
    `Guernsey`,
    `Guinea`,
    `Guinea-Bissau`,
    `Guyana`,
    `Haiti`,
    `Heard Island and McDonald Islands`,
    `Holy See (Vatican City State)`,
    `Honduras`,
    `Hong Kong`,
    `Hungary`,
    `Iceland`,
    `India`,
    `Indonesia`,
    `Iran, Islamic Republic of`,
    `Iraq`,
    `Ireland`,
    `Isle of Man`,
    `Italy`,
    `Jamaica`,
    `Japan`,
    `Jersey`,
    `Jordan`,
    `Kazakhstan`,
    `Kenya`,
    `Kiribati`,
    `Korea, Democratic People's Republic of`,
    `Korea, Republic of `,
    `Kosovo`,
    `Kuwait`,
    `Kyrgyzstan`,
    `Lao People's Democratic Republic`,
    `Latvia`,
    `Lebanon`,
    `Lesotho`,
    `Liberia`,
    `Libya`,
    `Liechtenstein`,
    `Lithuania`,
    `Luxembourg`,
    `Macao`,
    `Macedonia, the Former Yugoslav Republic of`,
    `Madagascar`,
    `Malawi`,
    `Malaysia`,
    `Maldives`,
    `Mali`,
    `Malta`,
    `Marshall Islands`,
    `Martinique`,
    `Mauritania`,
    `Mauritius`,
    `Mayotte`,
    `Mexico`,
    `Micronesia, Federated States of`,
    `Moldova, Republic of`,
    `Monaco`,
    `Mongolia`,
    `Montenegro`,
    `Montserrat`,
    `Morocco`,
    `Mozambique`,
    `Myanmar`,
    `Namibia`,
    `Nauru`,
    `Nepal`,
    `Netherlands`,
    `New Caledonia`,
    `New Zealand`,
    `Nicaragua`,
    `Niger`,
    `Nigeria`,
    `Niue`,
    `Norfolk Island`,
    `Northern Mariana Islands`,
    `Norway`,
    `Oman`,
    `Pakistan`,
    `Palau`,
    `Palestine, State of`,
    `Panama`,
    `Papua New Guinea`,
    `Paraguay`,
    `Peru`,
    `Philippines`,
    `Pitcairn`,
    `Poland`,
    `Portugal`,
    `Puerto Rico`,
    `Qatar`,
    `Réunion`,
    `Romania`,
    `Russia`,
    `Rwanda`,
    `Saint Barthélemy`,
    `Saint Helena, Ascension and Tristan da Cunha`,
    `Saint Kitts and Nevis`,
    `Saint Lucia`,
    `Saint Martin`,
    `Saint Pierre and Miquelon`,
    `Saint Vincent and the Grenadines`,
    `Samoa`,
    `San Marino`,
    `Sao Tome and Principe`,
    `Saudi Arabia`,
    `Senegal`,
    `Serbia`,
    `Seychelles`,
    `Sierra Leone`,
    `Singapore`,
    `Sint Maarten (Dutch part)`,
    `Slovakia`,
    `Slovenia`,
    `Solomon Islands`,
    `Somalia`,
    `South Africa`,
    `South Georgia and the South Sandwich Islands`,
    `South Sudan`,
    `Spain`,
    `Sri Lanka`,
    `Sudan`,
    `Suriname`,
    `Svalbard and Jan Maye`,
    `Swaziland`,
    `Sweden`,
    `Switzerland`,
    `Syrian Arab Republic`,
    `Taiwan, Province of China`,
    `Tajikistan`,
    `Tanzania, United Republic of`,
    `Thailand`,
    `Timor-Leste`,
    `Togo`,
    `Tokelau`,
    `Tonga`,
    `Trinidad and Tobago`,
    `Tunisia`,
    `Turkey`,
    `Turkmenistan`,
    `Turks and Caicos Islands`,
    `Tuvalu`,
    `Uganda`,
    `Ukraine`,
    `United Arab Emirates`,
    `United Kingdom`,
    `United States`,
    `United States Minor Outlying Islands`,
    `Uruguay`,
    `Uzbekistan`,
    `Vanuatu`,
    `Venezuela, Bolivarian Republic of`,
    `Viet Nam`,
    `Virgin Islands, British`,
    `Virgin Islands, U.S.`,
    `Wallis and Futuna`,
    `Western Sahara`,
    `Yemen`,
    `Zambia`,
    `Zimbabwe`,
  ].map((key) => ({ key, text: key, id: key }))
}

function getCountriesWithCodes(): OptionSelectorButton[] {
  return Object.entries({
    AF: 'Afghanistan',
    AX: 'Aland Islands',
    AL: 'Albania',
    DZ: 'Algeria',
    AS: 'American Samoa',
    AD: 'Andorra',
    AO: 'Angola',
    AI: 'Anguilla',
    AQ: 'Antarctica',
    AG: 'Antigua And Barbuda',
    AR: 'Argentina',
    AM: 'Armenia',
    AW: 'Aruba',
    AU: 'Australia',
    AT: 'Austria',
    AZ: 'Azerbaijan',
    BS: 'Bahamas',
    BH: 'Bahrain',
    BD: 'Bangladesh',
    BB: 'Barbados',
    BY: 'Belarus',
    BE: 'Belgium',
    BZ: 'Belize',
    BJ: 'Benin',
    BM: 'Bermuda',
    BT: 'Bhutan',
    BO: 'Bolivia',
    BA: 'Bosnia And Herzegovina',
    BW: 'Botswana',
    BV: 'Bouvet Island',
    BR: 'Brazil',
    IO: 'British Indian Ocean Territory',
    BN: 'Brunei Darussalam',
    BG: 'Bulgaria',
    BF: 'Burkina Faso',
    BI: 'Burundi',
    KH: 'Cambodia',
    CM: 'Cameroon',
    CA: 'Canada',
    CV: 'Cape Verde',
    KY: 'Cayman Islands',
    CF: 'Central African Republic',
    TD: 'Chad',
    CL: 'Chile',
    CN: 'China',
    CX: 'Christmas Island',
    CC: 'Cocos (Keeling) Islands',
    CO: 'Colombia',
    KM: 'Comoros',
    CG: 'Congo',
    CD: 'Congo, Democratic Republic',
    CK: 'Cook Islands',
    CR: 'Costa Rica',
    CI: "Cote D'Ivoire",
    HR: 'Croatia',
    CU: 'Cuba',
    CY: 'Cyprus',
    CZ: 'Czech Republic',
    DK: 'Denmark',
    DJ: 'Djibouti',
    DM: 'Dominica',
    DO: 'Dominican Republic',
    EC: 'Ecuador',
    EG: 'Egypt',
    SV: 'El Salvador',
    GQ: 'Equatorial Guinea',
    ER: 'Eritrea',
    EE: 'Estonia',
    ET: 'Ethiopia',
    FK: 'Falkland Islands (Malvinas)',
    FO: 'Faroe Islands',
    FJ: 'Fiji',
    FI: 'Finland',
    FR: 'France',
    GF: 'French Guiana',
    PF: 'French Polynesia',
    TF: 'French Southern Territories',
    GA: 'Gabon',
    GM: 'Gambia',
    GE: 'Georgia',
    DE: 'Germany',
    GH: 'Ghana',
    GI: 'Gibraltar',
    GR: 'Greece',
    GL: 'Greenland',
    GD: 'Grenada',
    GP: 'Guadeloupe',
    GU: 'Guam',
    GT: 'Guatemala',
    GG: 'Guernsey',
    GN: 'Guinea',
    GW: 'Guinea-Bissau',
    GY: 'Guyana',
    HT: 'Haiti',
    HM: 'Heard Island & Mcdonald Islands',
    VA: 'Holy See (Vatican City State)',
    HN: 'Honduras',
    HK: 'Hong Kong',
    HU: 'Hungary',
    IS: 'Iceland',
    IN: 'India',
    ID: 'Indonesia',
    IR: 'Iran, Islamic Republic Of',
    IQ: 'Iraq',
    IE: 'Ireland',
    IM: 'Isle Of Man',
    IL: 'Israel',
    IT: 'Italy',
    JM: 'Jamaica',
    JP: 'Japan',
    JE: 'Jersey',
    JO: 'Jordan',
    KZ: 'Kazakhstan',
    KE: 'Kenya',
    KI: 'Kiribati',
    KR: 'Korea',
    KW: 'Kuwait',
    KG: 'Kyrgyzstan',
    LA: "Lao People's Democratic Republic",
    LV: 'Latvia',
    LB: 'Lebanon',
    LS: 'Lesotho',
    LR: 'Liberia',
    LY: 'Libyan Arab Jamahiriya',
    LI: 'Liechtenstein',
    LT: 'Lithuania',
    LU: 'Luxembourg',
    MO: 'Macao',
    MK: 'Macedonia',
    MG: 'Madagascar',
    MW: 'Malawi',
    MY: 'Malaysia',
    MV: 'Maldives',
    ML: 'Mali',
    MT: 'Malta',
    MH: 'Marshall Islands',
    MQ: 'Martinique',
    MR: 'Mauritania',
    MU: 'Mauritius',
    YT: 'Mayotte',
    MX: 'Mexico',
    FM: 'Micronesia, Federated States Of',
    MD: 'Moldova',
    MC: 'Monaco',
    MN: 'Mongolia',
    ME: 'Montenegro',
    MS: 'Montserrat',
    MA: 'Morocco',
    MZ: 'Mozambique',
    MM: 'Myanmar',
    NA: 'Namibia',
    NR: 'Nauru',
    NP: 'Nepal',
    NL: 'Netherlands',
    AN: 'Netherlands Antilles',
    NC: 'New Caledonia',
    NZ: 'New Zealand',
    NI: 'Nicaragua',
    NE: 'Niger',
    NG: 'Nigeria',
    NU: 'Niue',
    NF: 'Norfolk Island',
    MP: 'Northern Mariana Islands',
    NO: 'Norway',
    OM: 'Oman',
    PK: 'Pakistan',
    PW: 'Palau',
    PS: 'Palestinian Territory, Occupied',
    PA: 'Panama',
    PG: 'Papua New Guinea',
    PY: 'Paraguay',
    PE: 'Peru',
    PH: 'Philippines',
    PN: 'Pitcairn',
    PL: 'Poland',
    PT: 'Portugal',
    PR: 'Puerto Rico',
    QA: 'Qatar',
    RE: 'Reunion',
    RO: 'Romania',
    RU: 'Russian Federation',
    RW: 'Rwanda',
    BL: 'Saint Barthelemy',
    SH: 'Saint Helena',
    KN: 'Saint Kitts And Nevis',
    LC: 'Saint Lucia',
    MF: 'Saint Martin',
    PM: 'Saint Pierre And Miquelon',
    VC: 'Saint Vincent And Grenadines',
    WS: 'Samoa',
    SM: 'San Marino',
    ST: 'Sao Tome And Principe',
    SA: 'Saudi Arabia',
    SN: 'Senegal',
    RS: 'Serbia',
    SC: 'Seychelles',
    SL: 'Sierra Leone',
    SG: 'Singapore',
    SK: 'Slovakia',
    SI: 'Slovenia',
    SB: 'Solomon Islands',
    SO: 'Somalia',
    ZA: 'South Africa',
    GS: 'South Georgia And Sandwich Isl.',
    ES: 'Spain',
    LK: 'Sri Lanka',
    SD: 'Sudan',
    SR: 'Suriname',
    SJ: 'Svalbard And Jan Mayen',
    SZ: 'Swaziland',
    SE: 'Sweden',
    CH: 'Switzerland',
    SY: 'Syrian Arab Republic',
    TW: 'Taiwan',
    TJ: 'Tajikistan',
    TZ: 'Tanzania',
    TH: 'Thailand',
    TL: 'Timor-Leste',
    TG: 'Togo',
    TK: 'Tokelau',
    TO: 'Tonga',
    TT: 'Trinidad And Tobago',
    TN: 'Tunisia',
    TR: 'Turkey',
    TM: 'Turkmenistan',
    TC: 'Turks And Caicos Islands',
    TV: 'Tuvalu',
    UG: 'Uganda',
    UA: 'Ukraine',
    AE: 'United Arab Emirates',
    GB: 'United Kingdom',
    US: 'United States',
    UM: 'United States Outlying Islands',
    UY: 'Uruguay',
    UZ: 'Uzbekistan',
    VU: 'Vanuatu',
    VE: 'Venezuela',
    VN: 'Viet Nam',
    VG: 'Virgin Islands, British',
    VI: 'Virgin Islands, U.S.',
    WF: 'Wallis And Futuna',
    EH: 'Western Sahara',
    YE: 'Yemen',
    ZM: 'Zambia',
    ZW: 'Zimbabwe',
  }).map(([key, text]: [string, string]) => ({
    key,
    text,
    id: key,
  }))
}

function getDayNums(): OptionSelectorButton[] {
  return Array.from({ length: 31 }, (_, i) => i + 1).map((num) => ({
    key: `${num}`,
    text: `${num}`,
    id: `${num}`,
  }))
}

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

function getMonths(): OptionSelectorButton[] {
  const abbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return MONTH_KEYS.map((key, i) => ({ key, text: abbreviations[i], id: abbreviations[i] }))
}

function getMonthsFull(): OptionSelectorButton[] {
  const fullNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ]
  return MONTH_KEYS.map((key, i) => ({ key, text: fullNames[i], id: key }))
}

function getYears(): OptionSelectorButton[] {
  const currentYear = new Date().getFullYear()
  return Array.from({ length: currentYear + 1 - 1900 }, (_, i) => i + 1900)
    .reverse()
    .map((num) => ({
      key: `${num}`,
      text: `${num}`,
      id: `${num}`,
    }))
}
